import type { ReactElement } from 'react';

import { CardFrame, CardNote, QueryBody, RANGE_NOTE } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { plural } from '@/lib/plural';

import { complianceQuery, formatShare, rangeProblem } from '../presentation';
import {
  COMPLIANCE_VIEWS,
  type Compliance,
  type ComplianceView,
  type TimeStudyFilters,
} from '../types';
import { useCompliance, useComplianceByDepartment } from '../use-time-study';

// Cumplimiento de plazos (G1-5, F1-11): por colaborador o por departamento (reunión
// 2026-10-27; el departamento sale de los puestos de cada persona en el LMS). Las etapas
// cerradas en el rango en plazo y fuera de plazo, las abiertas que hoy ya vencieron y
// las escaladas. Tabla en escritorio; en móvil cada fila se lee como tarjeta.

type Counts = Compliance['totals'];

interface Row {
  key: string;
  label: string;
  counts: Counts;
}

const VIEW: Record<
  ComplianceView,
  { button: string; title: string; first: string; path: string; foot: string }
> = {
  persona: {
    button: 'Por persona',
    title: 'Cumplimiento de plazos por colaborador',
    first: 'Persona',
    path: '/api/v1/quality/productivity/compliance',
    foot: 'Una etapa con varias personas cuenta para cada una y una sola vez en el total.',
  },
  departamento: {
    button: 'Por departamento',
    title: 'Cumplimiento de plazos por departamento',
    first: 'Departamento',
    path: '/api/v1/quality/productivity/compliance/departments',
    foot: 'El departamento sale de los puestos de cada persona en el LMS. Una etapa con varias personas de un departamento cuenta una vez para él y una sola vez en el total.',
  },
};

const COLUMNS: { label: string; value: (c: Counts) => string }[] = [
  { label: 'En plazo', value: (c) => String(c.onTime) },
  { label: 'Fuera de plazo', value: (c) => String(c.late) },
  { label: 'Vencidas abiertas', value: (c) => String(c.openOverdue) },
  { label: 'Escaladas', value: (c) => String(c.escalated) },
  { label: 'Total', value: (c) => String(c.total) },
  { label: '% en plazo', value: (c) => formatShare(c.onTimePct) },
];

function CountCells({ counts }: { counts: Counts }): ReactElement {
  return (
    <>
      {COLUMNS.map((col) => (
        <td key={col.label} data-label={col.label} className="t-num">
          {col.value(counts)}
        </td>
      ))}
    </>
  );
}

function ComplianceTable(p: {
  caption: string;
  first: string;
  rows: Row[];
  totals: Counts;
}): ReactElement {
  return (
    <div className="t-scroll">
      <table className="t t-cards cmp-table">
        <caption className="sr-only">{p.caption}</caption>
        <thead>
          <tr>
            <th scope="col">{p.first}</th>
            {COLUMNS.map((col) => (
              <th key={col.label} scope="col" className="t-num">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {p.rows.map((r) => (
            <tr key={r.key}>
              <td data-label={p.first} className="nm">
                {r.label}
              </td>
              <CountCells counts={r.counts} />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="cmp-total">
            <td data-label={p.first}>Total del período</td>
            <CountCells counts={p.totals} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const EMPTY = 'No hay etapas con plazo cerradas ni vencidas en el rango.';
const DEADLINES =
  'Plazo vigente de cada etapa en Notificaciones; la capacitación vence con la vigencia del documento. Las vencidas abiertas se cuentan a hoy.';

function Body(p: { rows: Row[]; totals: Counts; view: ComplianceView }): ReactElement {
  const v = VIEW[p.view];
  return (
    <>
      <ComplianceTable caption={v.title} first={v.first} rows={p.rows} totals={p.totals} />
      <p className="cardfoot">
        {DEADLINES} {v.foot}
      </p>
    </>
  );
}

export function ComplianceCard({
  filters,
  companyId,
  onViewChange,
}: {
  filters: TimeStudyFilters;
  companyId: string;
  onViewChange: (view: ComplianceView) => void;
}): ReactElement {
  const problem = rangeProblem(filters);
  const view = filters.cumpl;
  const byPerson = useCompliance(filters, {
    companyId,
    enabled: problem === null && view === 'persona',
  });
  const byDept = useComplianceByDepartment(filters, {
    companyId,
    enabled: problem === null && view === 'departamento',
  });
  const totals = (view === 'persona' ? byPerson : byDept).data?.totals;
  const v = VIEW[view];
  return (
    <CardFrame
      title={v.title}
      className="ts-full"
      count={
        totals !== undefined && totals.total > 0
          ? `${plural(totals.total, 'etapa', 'etapas')} · ${formatShare(totals.onTimePct)} en plazo`
          : undefined
      }
      actions={
        problem === null && (
          <ExportMenu
            compact
            path={`${v.path}/export`}
            query={complianceQuery(filters)}
            title={v.title}
          />
        )
      }
    >
      <div className="cb" style={{ paddingBottom: 0 }}>
        <div className="seg" role="group" aria-label="Ver cumplimiento">
          {COMPLIANCE_VIEWS.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={c === view}
              className={cn(c === view && 'on')}
              onClick={() => onViewChange(c)}
            >
              {VIEW[c].button}
            </button>
          ))}
        </div>
      </div>
      {problem !== null ? (
        <CardNote>{RANGE_NOTE}</CardNote>
      ) : view === 'persona' ? (
        <QueryBody query={byPerson} rows={4} isEmpty={(d) => d.rows.length === 0} emptyText={EMPTY}>
          {(d) => (
            <Body
              view={view}
              totals={d.totals}
              rows={d.rows.map((r) => ({
                key: r.person?.id ?? 'none',
                label: r.person?.name ?? 'Sin persona identificada',
                counts: r,
              }))}
            />
          )}
        </QueryBody>
      ) : (
        <QueryBody query={byDept} rows={4} isEmpty={(d) => d.rows.length === 0} emptyText={EMPTY}>
          {(d) => (
            <Body
              view={view}
              totals={d.totals}
              rows={d.rows.map((r) => ({
                key: r.department === null ? 'none' : String(r.department.id),
                label: r.department?.name ?? 'Sin departamento',
                counts: r,
              }))}
            />
          )}
        </QueryBody>
      )}
    </CardFrame>
  );
}
